import { create } from "zustand";

import { OrderStatusesEnumType } from "@sellar-io/api-v2/types";
import { PaginationState, OnChangeFn } from "@tanstack/react-table";
import { SellarCreditOrderSortType } from "./SellarCreditTabTypes";

export type EntityType = {
  id: number;
  name: string;
};

type SellarCreditOrderState = {
  business: EntityType | null;
  columnSort: SellarCreditOrderSortType;
  orderStatuses: OrderStatusesEnumType[];
  orderDateMin: string;
  orderDateMax: string;
  orderTotalMin: string;
  orderTotalMax: string;
  pagination: PaginationState;
  scrollOffset: number;
  search: string;
  user: EntityType | null;
};

type SellarCreditOrderActions = {
  setBusiness: (business: EntityType | null) => void;
  setColumnSort: (columnSort: SellarCreditOrderSortType) => void;
  setOrderStatuses: (orderStatuses: OrderStatusesEnumType[]) => void;
  setOrderDateMin: (orderDateMin: string) => void;
  setOrderDateMax: (orderDateMax: string) => void;
  setOrderTotalMin: (orderTotalMin: string) => void;
  setOrderTotalMax: (orderTotalMax: string) => void;
  setPagination: OnChangeFn<PaginationState>;
  setScrollOffset: (scrollOffset: number) => void;
  setSearch: (search: string) => void;
  setUser: (user: EntityType | null) => void;
  reset: () => void;
  setState: (state: SellarCreditOrderState) => void;
};

const initialState: SellarCreditOrderState = {
  business: null,
  columnSort: "orderId_desc",
  orderStatuses: [],
  orderDateMin: "",
  orderDateMax: "",
  orderTotalMin: "",
  orderTotalMax: "",
  pagination: {
    pageIndex: 0,
    pageSize: 25,
  },
  scrollOffset: 0,
  search: "",
  user: null,
};

export const useSellarCreditOrderStore = create<
  SellarCreditOrderState & SellarCreditOrderActions
>((set) => ({
  ...initialState,
  setBusiness: (business: EntityType | null) => set({ business }),
  setColumnSort: (columnSort: SellarCreditOrderSortType) => set({ columnSort }),
  setOrderStatuses: (orderStatuses: OrderStatusesEnumType[]) =>
    set({ orderStatuses }),
  setOrderDateMin: (orderDateMin: string) => set({ orderDateMin }),
  setOrderDateMax: (orderDateMax: string) => set({ orderDateMax }),
  setOrderTotalMin: (orderTotalMin: string) => set({ orderTotalMin }),
  setOrderTotalMax: (orderTotalMax: string) => set({ orderTotalMax }),
  setPagination: (updater) => {
    set((state) => {
      const prev = state.pagination;
      const next = typeof updater === "function" ? updater(prev) : updater;
      return { pagination: next };
    });
  },
  setScrollOffset: (scrollOffset: number) => set({ scrollOffset }),
  setSearch: (search: string) => set({ search }),
  setUser: (user: EntityType | null) => set({ user }),
  reset: () => set(initialState),
  setState: (state) => set(state),
}));
